import { useEffect, useState } from 'react';

// Screen size breakpoints (matching Tailwind's default breakpoints)
export const screenSizes = {
  xs: 480,  // Extra small screens (phones < 480px)
  sm: 640,  // Small screens (large phones/small tablets)
  md: 768,  // Medium screens (tablets)
  lg: 1024, // Large screens (laptops)
  xl: 1280, // Extra large screens (desktops)
  '2xl': 1536 // 2XL screens (large desktops)
};

// Height breakpoints for more comprehensive responsive design
export const screenHeights = {
  xs: 667,  // Small phone heights (iPhone SE)
  sm: 812,  // Medium phone heights (iPhone X/11/12/13)
  md: 926,  // Large phone heights (iPhone Pro Max models)
  lg: 1080, // Tablet heights
  xl: 1366, // Laptop heights
  '2xl': 1600 // Large desktop heights
};

// Screen size detection function with proper ranges
export const getScreenSize = (): keyof typeof screenSizes | null => {
  if (typeof window === 'undefined') return null;
  
  const width = window.innerWidth;
  
  if (width < screenSizes.xs) return 'xs';
  if (width >= screenSizes.xs && width < screenSizes.sm) return 'sm';
  if (width >= screenSizes.sm && width < screenSizes.md) return 'md';
  if (width >= screenSizes.md && width < screenSizes.lg) return 'lg';
  if (width >= screenSizes.lg && width < screenSizes.xl) return 'xl';
  return '2xl';
};

// Screen height detection function with proper ranges
export const getScreenHeight = (): keyof typeof screenHeights | null => {
  if (typeof window === 'undefined') return null;
  
  const height = window.innerHeight;
  
  if (height < screenHeights.xs) return 'xs';
  if (height >= screenHeights.xs && height < screenHeights.sm) return 'sm';
  if (height >= screenHeights.sm && height < screenHeights.md) return 'md';
  if (height >= screenHeights.md && height < screenHeights.lg) return 'lg';
  if (height >= screenHeights.lg && height < screenHeights.xl) return 'xl';
  return '2xl';
};

// Combined screen size detection that considers both width and height
// Returns the smaller of the two to ensure proper fitting on all dimensions
export const getCombinedScreenSize = (): keyof typeof screenSizes | null => {
  if (typeof window === 'undefined') return null;
  
  const widthSize = getScreenSize();
  const heightSize = getScreenHeight();
  
  if (!widthSize || !heightSize) return null;
  
  // Map sizes to numerical values for comparison
  const sizeValues: { [key: string]: number } = { 
    'xs': 0, 'sm': 1, 'md': 2, 'lg': 3, 'xl': 4, '2xl': 5 
  };
  
  // Return the smaller of the two sizes to ensure content fits in both dimensions
  return sizeValues[widthSize] <= sizeValues[heightSize] ? widthSize : heightSize as keyof typeof screenSizes;
};

// Card size mapping based on screen size
export const cardSizes = {
  xs: { width: 'w-12', height: 'h-20' },   // Smallest screens
  sm: { width: 'w-14', height: 'h-24' },   // Small screens
  md: { width: 'w-16', height: 'h-28' },   // Medium screens
  lg: { width: 'w-20', height: 'h-32' },   // Large screens
  xl: { width: 'w-24', height: 'h-36' },   // Extra large screens
  '2xl': { width: 'w-28', height: 'h-40' } // 2XL screens
};

// More granular responsive size adjustments for empty card slots
export const emptySlotSize = {
  xs: { width: 'w-12', height: 'h-20' },
  sm: { width: 'w-14', height: 'h-24' },
  md: { width: 'w-16', height: 'h-28' },
  lg: { width: 'w-18', height: 'h-30' },
  xl: { width: 'w-20', height: 'h-32' },
  '2xl': { width: 'w-22', height: 'h-34' },
};

// More granular responsive size adjustments for filled card slots
export const filledSlotSize = {
  xs: { width: 'w-14', height: 'h-22' },
  sm: { width: 'w-16', height: 'h-24' },
  md: { width: 'w-18', height: 'h-28' },
  lg: { width: 'w-20', height: 'h-32' },
  xl: { width: 'w-24', height: 'h-36' },
  '2xl': { width: 'w-28', height: 'h-40' },
};

// Element spacing based on screen size
export const spacing = {
  xs: { gap: 'gap-1', padding: 'p-1' },
  sm: { gap: 'gap-2', padding: 'p-2' },
  md: { gap: 'gap-3', padding: 'p-3' },
  lg: { gap: 'gap-4', padding: 'p-4' },
  xl: { gap: 'gap-5', padding: 'p-5' },
  '2xl': { gap: 'gap-6', padding: 'p-6' }
};

// Card rotation angles for fan effect
export const cardRotation = {
  xs: 0,  // Smallest screens (minimal rotation)
  sm: 0,  // Small screens
  md: 0,  // Medium screens
  lg: 0,  // Large screens
  xl: 0,  // Extra large screens
  '2xl': 0 // 2XL screens (maximum rotation)
};

// React hook for responsive design
export const useResponsive = () => {
  const [screenSize, setScreenSize] = useState<keyof typeof screenSizes | null>(
    typeof window !== 'undefined' ? getCombinedScreenSize() : null
  );

  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setScreenSize(getCombinedScreenSize());
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    // Set initial value
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Simple utility functions
  const isMobile = screenSize === 'xs' || screenSize === 'sm';
  const isTablet = screenSize === 'md' || screenSize === 'lg';
  const isDesktop = screenSize === 'xl' || screenSize === '2xl';
  
  // Get sizes based on current screen size
  const getCardSize = () => screenSize ? cardSizes[screenSize] : cardSizes.md;
  const getEmptySlotSize = () => screenSize ? emptySlotSize[screenSize] : emptySlotSize.md;
  const getFilledSlotSize = () => screenSize ? filledSlotSize[screenSize] : filledSlotSize.md;
  const getSpacing = () => screenSize ? spacing[screenSize] : spacing.md;
  const getRotation = () => screenSize ? cardRotation[screenSize] : cardRotation.md;

  return {
    screenSize,
    dimensions,
    widthSize: getScreenSize(),
    heightSize: getScreenHeight(),
    isMobile,
    isTablet, 
    isDesktop,
    getCardSize,
    getEmptySlotSize,
    getFilledSlotSize,
    getSpacing,
    getRotation,
    // Aspect ratio utility
    isLandscape: dimensions.width > dimensions.height,
    isPortrait: dimensions.width <= dimensions.height
  };
};
